import { useCallback } from 'react'
import { useSnackbar } from 'notistack'
import { t } from '@lingui/macro'

import { useUpdatePatientMutation } from '@/core/services/patient'

import { PatientProfileFormData } from './usePatientProfileForm'
import { useRunInsVerification } from './useRunInsVerification'

type Params = {
  isINSEnabled: boolean
  initialValues?: Partial<PatientProfileFormData>
  patientId?: string
}

export function useVerifyInsBeforeUpdate({ isINSEnabled, initialValues, patientId }: Params) {
  const { enqueueSnackbar } = useSnackbar()
  const [commitPatientUpdate] = useUpdatePatientMutation()
  const { canRunInsVerification, runInsVerification } = useRunInsVerification()

  return useCallback(
    async (data: PatientProfileFormData): Promise<boolean> => {
      if (!isINSEnabled || !canRunInsVerification) return true

      const hasInsData = Boolean(data.insNIR && data.insOID)
      if (!hasInsData) return true

      const initialInsNIR = initialValues?.insNIR ?? ''
      const currentInsNIR = data.insNIR
      const initialInsOID = initialValues?.insOID ?? ''

      const insChanged = currentInsNIR !== initialInsNIR || data.insOID !== initialInsOID
      if (!insChanged) return true

      const hasRequiredIdentityTraits =
        Boolean(data.surname || data.birthSurname) && Boolean(data.birthFirstName) && Boolean(data.birthday) && Boolean(data.gender)
      if (!hasRequiredIdentityTraits) return true

      const result = await runInsVerification(data, { patientId })

      if (result === 'OK') {
        enqueueSnackbar(t`INS verified successfully. Patient information will be updated.`, { variant: 'success' })
        return true
      }

      if (result === 'KO') {
        if (patientId) {
          await commitPatientUpdate({ id: patientId, insNIR: null, insOID: null }).unwrap()
        }
        enqueueSnackbar(t`The INS could not be validated`, { variant: 'error' })
        return false
      }

      enqueueSnackbar(t`Check INS identity failed due to unknown error`, { variant: 'error' })
      return false
    },
    [canRunInsVerification, commitPatientUpdate, enqueueSnackbar, initialValues, isINSEnabled, patientId, runInsVerification]
  )
}
